import { Link as LinkIcon, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export interface AddLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (link: { url: string; label: string }) => void;
  loading?: boolean;
}

/** Anything without a scheme is assumed https, matching how people paste URLs. */
function withScheme(raw: string) {
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

export function AddLinkDialog({ open, onOpenChange, onAdd, loading = false }: AddLinkDialogProps) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setUrl('');
    setLabel('');
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = () => {
    const candidate = withScheme(url.trim());
    let parsed: URL;
    try {
      parsed = new URL(candidate);
    } catch {
      setError('That does not look like a URL.');
      return;
    }
    onAdd({ url: parsed.toString(), label: label.trim() || parsed.hostname });
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-bold uppercase tracking-wider text-sm">
            <LinkIcon className="w-4 h-4 text-primary" />
            Save Link
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Keep a related page alongside this one, so it's here next time you visit.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label
              htmlFor="link-url"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
            >
              URL
            </Label>
            <Input
              id="link-url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
              }}
              placeholder="https://example.com/docs"
              className="h-9 text-xs"
            />
            {error && <p className="text-[11px] text-destructive">{error}</p>}
          </div>
          <div className="grid gap-2">
            <Label
              htmlFor="link-label"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
            >
              Label
            </Label>
            <Input
              id="link-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Defaults to the hostname"
              className="h-9 text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={loading || !url.trim()}>
            {loading && <Loader2 className="w-3 h-3 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
